import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  CaretDown,
  Check,
  Microphone,
  MicrophoneSlash,
  SpeakerHigh,
  VideoCamera,
  VideoCameraSlash,
  X,
} from '@phosphor-icons/react';
import { calls as callsApi, messages as messagesApi, users as usersApi } from '../api/endpoints';
import {
  hautParleurDisponible,
  reglerHautParleur,
  relacherHautParleur,
  suivreLeSon,
} from '../firebase/hautParleur';
import { placerLesVues } from '../firebase/appelNatif';
import { arreterLaSonnerie, demarrerLaSonnerie } from '../firebase/sonnerie';
import {
  accepterLAppelSysteme,
  appelSystemeAttendu,
  appelSystemeQuiSonne,
  connecterLAppelSysteme,
  fermerLAppelSysteme,
  ouvrirLAppelSysteme,
  refuserLAppelSysteme,
  suivreLAppelSysteme,
} from '../firebase/appelSysteme';
import { SONNERIE_MAX_MS } from '../firebase/calls';
import type { ActiveCall, CallStatus, IncomingCall } from '../firebase/calls';
import { Avatar } from '../components/ui/Avatar';
import { BulleAppel } from '../components/ui/BulleAppel';
import { useSession } from './SessionContext';
import { cadreZoome, pincer, promener, SANS_ZOOM, type Zoom } from '../utils/zoomPince';
import { t } from '../i18n';

/**
 * L'appel, partout dans l'application.
 *
 * Il vit au-dessus des ecrans parce qu'il leur survit : on peut appeler
 * depuis un profil, puis se promener dans le feed pendant la
 * conversation. La banniere d'appel entrant, elle, doit pouvoir apparaitre
 * quel que soit l'ecran ouvert.
 *
 * Une seule ecoute est posee, tant qu'un membre est connecte, sur ce qui
 * sonne chez lui. C'est ce qui fait apparaitre un appel entrant — et c'est
 * aussi pourquoi il n'en apparait aucun quand l'application est fermee :
 * voir `firebase/calls.ts`.
 */

/** Le peu qu'il faut savoir de l'autre pour l'afficher pendant l'appel. */
interface Correspondant {
  id: string;
  username: string;
  avatarUrl: string | null;
}

interface CallApi {
  /** Appelle un membre. Nom et photo servent a l'afficher tout de suite. */
  appeler: (correspondant: Correspondant, avecVideo?: boolean) => Promise<void>;
  /** Un appel est-il en cours ? Sert a griser un second bouton. */
  occupe: boolean;
}

const Contexte = createContext<CallApi | null>(null);

export function useCall(): CallApi {
  const valeur = useContext(Contexte);
  if (!valeur) throw new Error('useCall doit vivre dans un CallProvider');
  return valeur;
}

/**
 * Raccrocher, c'est reposer le combine.
 *
 * Une croix disait « annuler », pas « fin d'appel ». Le combine repose
 * dit la fin — le signe qu'on lit sans y penser sur tous les telephones.
 *
 * C'est bien le dessin de Phosphor, et non un dessin de plus : celui de
 * `PhoneDisconnect`, dont on ne garde que le combine. Son trait de socle
 * ajoutait un second signe la ou un seul se lit mieux, et le combine y est
 * franc et large — la ou `Phone` bascule d'un huitieme de tour restait
 * mince et penche, ce qui se lisait mal sur un disque rouge.
 */
const COMBINE_REPOSE =
  'M236.28,161.84a16,16,0,0,1-18.38,5.06l-49-17.39-.29-.11a16,16,0,0,1-9.72-11.59l-6.21-29.75h0a76.52,76.52,0,0,0-49.68.11l-5.9,29.52a16,16,0,0,1-9.75,11.73l-.29.11-49,17.37A15.8,15.8,0,0,1,32.35,168a16,16,0,0,1-12.63-6.14c-17.23-22.22-15.3-51.71,4.69-71.71,56.15-56.17,151-56.17,207.18,0h0C251.58,110.13,253.51,139.62,236.28,161.84Z';

function Combine({ size = 25 }: { size?: number }) {
  return (
    <svg viewBox="0 0 256 256" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d={COMBINE_REPOSE} />
    </svg>
  );
}

/**
 * Au-dela, on n'attend plus le systeme.
 *
 * L'envoi VoIP met une seconde ou deux ; il peut aussi ne jamais arriver
 * — un membre qui a coupe les appels dans ses reglages n'en recoit aucun.
 * Passe ce delai, la banniere de l'application sonne seule, comme avant.
 */
const ATTENTE_SYSTEME_MS = 3000;

/**
 * La vignette de retour : sa taille, et la marge qui la decolle du bord.
 *
 * Les memes chiffres que dans la feuille de style, et il le faut : c'est
 * l'ecran qui calcule desormais ou elle se pose, parce qu'aucune regle CSS
 * ne sait ou finit un nom de longueur libre ni ou commence la rangee des
 * commandes.
 */
const VIGNETTE = { largeur: 104, hauteur: 150, marge: 14 };

/** Les quatre coins, en haut ou en bas, a gauche ou a droite. */
type Coin = 'hg' | 'hd' | 'bg' | 'bd';

/** Ce que l'ecran mesure pour poser la vignette : les deux hauteurs et la largeur. */
interface Bornes {
  haut: number;
  bas: number;
  large: number;
}

/** Au-dela de ce deplacement, le doigt transporte la vignette au lieu
    d'echanger les deux images. */
const GLISSEMENT_MIN = 8;

/** Deux appuis plus rapproches que cela, au meme endroit, font un double appui. */
const DOUBLE_APPUI = { ms: 350, px: 40 };

/** Revenu presque a un, le zoom se remet exactement a un. */
const ZOOM_RETOUR = 1.05;

/** "01:23" a partir d'un nombre de secondes. */
function duree(secondes: number): string {
  const m = Math.floor(secondes / 60);
  const s = secondes % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();

  const [entrant, setEntrant] = useState<(IncomingCall & Correspondant) | null>(null);
  const [actif, setActif] = useState<ActiveCall | null>(null);
  const [statut, setStatut] = useState<CallStatus | null>(null);
  const [qui, setQui] = useState<Correspondant | null>(null);
  const [connecte, setConnecte] = useState(false);
  const [muet, setMuet] = useState(false);
  const [sansCamera, setSansCamera] = useState(false);
  /*
    La camera de l'autre est-elle ouverte ?

    Le flux ne le dit pas : une piste coupee continue d'arriver, en noir,
    et une camera jamais ouverte n'arrive pas du tout. C'est la base qui
    porte le drapeau, et l'ecran met un visage a la place du rectangle
    vide.
  */
  const [cameraEnFace, setCameraEnFace] = useState(false);
  /*
    Ma camera est-elle ouverte ?

    A ne pas confondre avec `video`, qui dit seulement que l'appel montre
    des images — celles de l'autre suffisent. Les deux boutons se
    decidaient sur `video`, si bien que celui qui recevait une montee en
    visio se voyait proposer de *couper* une camera qu'il n'avait jamais
    ouverte : le bouton ne faisait alors rien du tout.
  */
  const [maCamera, setMaCamera] = useState(false);
  /* La video n'est plus decidee au depart : elle peut arriver en cours
     d'appel. C'est donc un etat, pas une propriete de l'appel. */
  const [video, setVideo] = useState(false);
  const [hautParleur, setHautParleur] = useState(false);
  /* La sortie choisie, telle qu'elle doit etre reposee apres coup. */
  const routeVoulueRef = useRef(false);
  const [reduit, setReduit] = useState(false);
  const [secondes, setSecondes] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);

  /* Les rappels de l'appel sont figes a sa creation : ils lisent l'appel
     en cours par reference plutot que par fermeture. */
  const actifRef = useRef<ActiveCall | null>(null);
  actifRef.current = actif;
  /* Un appel ne laisse qu'une trace, quel que soit le bout qui raccroche. */
  const noteFaiteRef = useRef(false);
  /* La duree lue par un rappel fige : elle change a chaque seconde. */
  const secondesRef = useRef(0);
  secondesRef.current = secondes;
  const connecteRef = useRef(false);
  connecteRef.current = connecte;
  /* La trace dit le moyen sur lequel l'appel s'est termine, pas celui sur
     lequel il a commence : une montee en visio compte. */
  const videoRef = useRef(false);
  videoRef.current = video;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  /*
    En natif, les deux images sont dessinees par l'application, sous la
    vue web. La page n'en garde que les emplacements : elle seule sait ce
    que vaut une encoche ou une marge, et le natif s'y conforme. C'est
    aussi ce qui rend l'echange gratuit — permuter les deux rectangles
    suffit, sans une ligne de Swift.
  */
  const grandRef = useRef<HTMLDivElement | null>(null);
  const petitRef = useRef<HTMLButtonElement | null>(null);
  const [moiEnGrand, setMoiEnGrand] = useState(false);
  /* La camera de l'ecran ou celle du dos : le miroir n'est que pour la premiere. */
  const [cameraAvant, setCameraAvant] = useState(true);
  const dernierAppuiRef = useRef<{ t: number; x: number; y: number } | null>(null);
  /*
    Le zoom a deux doigts sur la grande image.

    Tenu hors de React, comme le glissement de la vignette : il change a
    chaque image pendant le geste, et un rendu de tout l'ecran d'appel
    soixante fois par seconde pour deplacer une image n'aurait aucun sens.
  */
  const zoomRef = useRef<Zoom>(SANS_ZOOM);
  const doigtsRef = useRef(new Map<number, { x: number; y: number }>());
  const departZoomRef = useRef<{
    z: Zoom;
    /* Deux doigts : leur ecart et leur milieu ; un seul : sa position. */
    ecart: number;
    point: { x: number; y: number };
  } | null>(null);
  /* Le geste a-t-il bouge : alors ce n'etait pas un appui. */
  const bougeRef = useRef(false);
  const imageZoomRef = useRef(0);
  /*
    Tant que l'autre n'a pas decroche, il n'y a rien a montrer de son cote.

    C'est donc ma propre image qui prend la grande place — comme dans
    l'ecran d'appel du systeme, ou l'on se voit pendant que ca sonne. Elle
    lui rend la place des qu'il repond, a moins que je n'aie echange les
    deux moi-meme entre-temps.
  */
  const grandEstMoi = moiEnGrand || (maCamera && !connecte);

  const distanteRef = useRef<HTMLVideoElement | null>(null);
  const mienneRef = useRef<HTMLVideoElement | null>(null);

  /*
    La vignette se pose sous le nom, et se deplace au doigt.

    En video le nom passe tout en haut, sur l'image ; la vignette de
    l'autre etait juste a cote, et un nom un peu long lui passait dessus.
    Les deux ne peuvent pas se partager la meme bande : on empile.

    Rien en CSS ne sait ou finit un texte qui peut tenir sur une ligne
    comme sur deux, ni ou commence la rangee des commandes. On mesure donc
    les deux blocs, et l'on donne a la feuille de style la position de la
    vignette en clair. Trois choses la suivent : la vignette, la doublure
    qui la recouvre quand la camera est coupee, et le bouton transparent
    qui echange les deux images.
  */
  const nomRef = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLDivElement | null>(null);
  const [bornes, setBornes] = useState<Bornes | null>(null);
  /* Le coin ou la vignette se range, et le decalage du doigt qui la
     transporte d'un coin a l'autre. */
  const [coin, setCoin] = useState<Coin>('hd');
  const [glisse, setGlisse] = useState<{ dx: number; dy: number } | null>(null);
  const departRef = useRef<{ x: number; y: number; bouge: boolean } | null>(null);

  /* --- Ce qui sonne ------------------------------------------------- */

  /*
    L'appel qu'on vient de prendre.

    Repondre efface l'annonce dans `incomingCalls` — le meme effacement
    que lorsque l'appelant renonce. Sans ce repere, l'ecoute lisait notre
    propre reponse comme un abandon et raccrochait aupres de CallKit
    l'appel qui venait de commencer : ecran ferme d'un coup cote appele,
    session audio emportee avec lui, et l'appelant voyait « repondu, puis
    raccroche ». Repondre depuis la banniere du systeme sans ouvrir
    l'application donnait la meme chose en silence — l'appel tenait, mais
    sans son, CallKit ayant repris sa session.
  */
  const prisRef = useRef<string | null>(null);

  /*
    L'appel qui attend que le systeme sonne aussi.

    La page lit la base en direct ; l'envoi VoIP, lui, fait le tour par
    Apple. Notre banniere arrivait donc une a deux secondes avant celle du
    systeme — et decrocher dans cet intervalle laissait CallKit sonner
    par-dessus une conversation commencee. Son anneau interrompt la
    session audio : plus un bruit tant qu'on n'avait pas accepte aussi la
    banniere du haut.

    On attend donc que le systeme ait annonce le meme appel. L'attente ne
    dure pas : elle tombe d'elle-meme si l'envoi n'arrive jamais — un
    membre qui a coupe les appels dans ses reglages n'en recoit aucun, et
    doit tout de meme voir son telephone sonner.
  */
  const attenteRef = useRef<{ appel: IncomingCall; minuteur: number } | null>(null);

  const oublierLAttente = useCallback(() => {
    const attente = attenteRef.current;
    if (!attente) return;
    window.clearTimeout(attente.minuteur);
    attenteRef.current = null;
  }, []);

  /** Montre l'appel. Le nom et la photo suivent la banniere. */
  const montrerLAppel = useCallback(
    (appel: IncomingCall) => {
      oublierLAttente();
      setEntrant({ ...appel, id: appel.callerId, username: t.appels.ecran.quelquun, avatarUrl: null });
      void usersApi
        .publicProfile(appel.callerId)
        .then((profil) => {
          const connu = {
            id: appel.callerId,
            username: profil.user.username,
            avatarUrl: profil.user.avatarUrl,
          };
          setEntrant((e) => (e ? { ...e, ...connu } : e));
          /*
            Et l'appel deja decroche, s'il l'a ete entre-temps.

            Decrocher fige le correspondant tel qu'on le connaissait — soit
            « Quelqu'un », quand la reponse vient du bouton vert du systeme
            et devance la lecture du profil. L'ecran d'appel affichait alors
            un inconnu sans visage pendant toute la conversation.
          */
          setQui((q) => (q && q.id === appel.callerId ? { ...q, ...connu } : q));
        })
        .catch(() => undefined);
    },
    [oublierLAttente],
  );

  useEffect(() => {
    if (!user) return;
    return callsApi.watch((liste) => {
      const premier = liste[0];
      if (!premier) {
        oublierLAttente();
        setEntrant(null);
        /*
          L'appelant a raccroche avant qu'on decroche.

          La banniere de l'application disparaissait bien, mais l'ecran
          d'appel du systeme restait : CallKit ne sait rien de la base, il
          ne connait que ce qu'on lui dit. Il faut donc le lui dire — sauf
          si l'annonce a disparu parce que nous venons de la prendre.
        */
        if (!prisRef.current) void fermerLAppelSysteme();
        return;
      }
      /*
        Deja en ligne : on refuse tout de suite.

        Faire sonner un second appel par-dessus le premier n'a pas de sens
        — une seule connexion existe, et decrocher aurait ecrase celle en
        cours. Le refus previent l'autre bout immediatement, ce qui vaut
        mieux qu'une sonnerie dans le vide jusqu'a l'expiration.
      */
      if (actifRef.current) {
        void callsApi.decline(premier.callId).catch(() => undefined);
        return;
      }
      // Deja en attente du meme appel : la base peut se repeter.
      if (attenteRef.current?.appel.callId === premier.callId) return;

      // La ou il n'y a pas d'appel systeme, il n'y a rien a attendre.
      if (!appelSystemeAttendu()) return montrerLAppel(premier);

      attenteRef.current = {
        appel: premier,
        minuteur: window.setTimeout(() => montrerLAppel(premier), ATTENTE_SYSTEME_MS),
      };
      // Il a peut-etre deja sonne : l'application se lance parfois
      // pendant que le telephone sonne, et l'evenement est alors passe.
      void appelSystemeQuiSonne().then((callId) => {
        if (callId && attenteRef.current?.appel.callId === callId) montrerLAppel(premier);
      });
    });
  }, [user, montrerLAppel, oublierLAttente]);

  /*
    Le son de l'autre, pose sur la balise — et relance.

    `srcObject` ne se passe pas en attribut, d'ou le passage par la
    reference. Mais surtout : le flux est pose avant qu'aucune piste n'y
    soit, puisqu'il est cree vide et rempli quand l'autre bout repond.
    WebKit tient alors la balise pour vide et ne repart pas tout seul
    quand une piste s'y ajoute — l'appel s'etablissait, et personne
    n'entendait rien. Reposer la source et redemander la lecture a chaque
    arrivee de piste est ce qui remet le son.
  */
  const poserFlux = useCallback(() => {
    const appel = actifRef.current;
    if (!appel) return;
    /*
      Rien a poser pour un appel natif, et surtout rien a lancer.

      La voix de l'autre est jouee par la bibliotheque native. Une balise
      qui se met a jouer, meme un flux vide, fait ouvrir a WebKit une
      session audio pour sa lecture — soit exactement le proprietaire
      qu'on vient de mettre a la porte, et une occasion de plus de
      reposer une route par-dessus la notre.
    */
    if (appel.natif) return;
    for (const balise of [audioRef.current, distanteRef.current]) {
      if (!balise) continue;
      /*
        Reposer la meme source ne suffit pas : WebKit ne reevalue que si
        elle change vraiment. C'est pourtant ce cas-la qu'il faut forcer —
        le flux etait vide quand on l'a pose. On le detache donc d'abord,
        et seulement quand la balise ne joue pas : la couper en pleine
        conversation ferait un trou dans le son.
      */
      if (balise.srcObject === appel.remote && balise.paused) balise.srcObject = null;
      balise.srcObject = appel.remote;
      void balise.play().catch(() => undefined);
    }
    if (mienneRef.current) {
      mienneRef.current.srcObject = appel.local;
      void mienneRef.current.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (!actif) return;
    poserFlux();
    // `video` compte dans les dependances : les balises video n'existent
    // qu'apres la bascule, et une source posee avant elles serait perdue.
  }, [actif, video, poserFlux]);

  /*
    La route du son est posee, jamais subie.

    La vue web sort par le haut-parleur des que le micro s'ouvre. Le
    bouton demarrait donc en position « eteint » alors qu'il l'etait deja,
    et l'allumer ne changeait rien — il pretendait activer ce qui
    fonctionnait.

    Un appel vocal commence maintenant sur l'ecouteur, comme un vrai
    telephone : on le porte a l'oreille, et le bouton sert vraiment. Un
    appel video, lui, part au haut-parleur — on ne tient pas un telephone
    contre son oreille pour regarder quelqu'un.
  */
  const poserLaRoute = useCallback((voulu: boolean) => {
    routeVoulueRef.current = voulu;
    setHautParleur(voulu);
    void reglerHautParleur(voulu)
      .then((r) => {
        // Le natif rend ce qu'il a obtenu : l'ecran suit la realite, il
        // ne prétend pas. Un bouton qui ment est pire qu'un bouton absent.
        if (!r) return;
        setHautParleur(r.obtenu);
      })
      .catch((cause: unknown) =>
        setErreur(
          t.appels.ecran.son(cause instanceof Error ? cause.message : t.appels.ecran.natifMuet),
        ),
      );
  }, []);

  useEffect(() => {
    if (!actif) return;
    poserLaRoute(video);
  }, [actif, video, poserLaRoute]);

  /*
    La route est reposee une fois la voix passee : WebKit reconfigure la
    session au moment ou le flux distant arrive, et ecraserait ce qu'on a
    demande avant.

    Ce qu'on repose, c'est le choix du moment — pas celui du depart. Les
    commandes s'affichent desormais des l'appui sur « appeler » : mettre le
    haut-parleur pendant que ca sonne est devenu possible, et reposer
    aveuglement la route d'origine le defaisait a la seconde ou l'autre
    decrochait.
  */
  useEffect(() => {
    if (!actif || !connecte) return;
    poserLaRoute(routeVoulueRef.current);
  }, [actif, connecte, poserLaRoute]);

  /*
    On regarde la session pendant tout l'appel, pas seulement au reglage.

    Ce qu'on demande et ce qu'on obtient sont deux choses : WebKit repose
    sa configuration quand le flux s'ouvre puis quand la piste distante
    arrive, et l'ecran affichait paisiblement la route du debut. Le natif
    pousse desormais chaque changement avec sa raison — c'est ce qui doit
    designer qui remet le haut-parleur.
  */
  useEffect(() => {
    if (!actif) return;
    const abonnement = suivreLeSon((etat) => setHautParleur(etat.hautParleur));
    if (!abonnement) return;
    return () => void abonnement.then((h) => h.remove()).catch(() => undefined);
  }, [actif]);

  /*
    Ou se pose la vignette, en clair.

    Le coin ou elle se range decide de ses deux coordonnees ; le doigt qui
    la transporte y ajoute son decalage, remis a zero des qu'il la lache.
    Les quatre coins bornent le voyage : le doigt peut sortir de l'ecran,
    la vignette non — elle passait sous l'encoche et debordait par le
    cote. Tant que rien n'est mesure, la feuille de style garde sa position
    de repli, le coin haut droit comme avant.
  */
  const placeDeLaVignette = useCallback(
    (ou: Coin, decalage: { dx: number; dy: number } | null) => {
      if (!bornes) return null;
      const droite = bornes.large - VIGNETTE.marge - VIGNETTE.largeur;
      const entre = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
      return {
        x: entre((ou[1] === 'g' ? VIGNETTE.marge : droite) + (decalage?.dx ?? 0), VIGNETTE.marge, droite),
        y: entre((ou[0] === 'h' ? bornes.haut : bornes.bas) + (decalage?.dy ?? 0), bornes.haut, bornes.bas),
      };
    },
    [bornes],
  );

  const vignette = placeDeLaVignette(coin, glisse);

  /*
    Quelle place n'a pas d'image a montrer : un portrait la remplace (voir
    « Un visage a la place du noir », plus bas). Calcule ici parce que le
    placement des images natives en depend.
  */
  const monImageManque = !maCamera || sansCamera;
  /* Avant la connexion il n'y a rien a montrer de son cote : la vignette
     etait noire pendant toute la sonnerie d'un appel video. */
  const sonImageManque = !connecte || !cameraEnFace;
  const grandSansImage = grandEstMoi ? monImageManque : sonImageManque;
  const petitSansImage = grandEstMoi ? sonImageManque : monImageManque;

  /*
    Le zoom ne survit pas a un changement de grande image.

    Il valait pour ce qu'on regardait : echanger les deux images, voir la
    camera d'en face se couper ou l'appel se terminer, c'est regarder autre
    chose, et le retrouver agrandi surprendrait. Declare avant le placement
    des images natives, qui le relit dans la foulee.
  */
  useEffect(() => {
    zoomRef.current = SANS_ZOOM;
    departZoomRef.current = null;
    doigtsRef.current.clear();
    for (const balise of [distanteRef.current, mienneRef.current]) {
      if (balise) balise.style.transform = '';
    }
  }, [grandEstMoi, grandSansImage, video, actif]);

  /*
    Ou les images natives se posent, a l'instant present.

    Sortie de son effet : le geste qui deplace la vignette doit la reposer
    a chaque frame, et recreer tout l'effet — ses observateurs, ses
    relances, l'attribut qui efface la page — soixante fois par seconde
    pour cela n'aurait aucun sens.
  */
  const placerLesImages = useCallback(() => {
    const grand = grandRef.current?.getBoundingClientRect();
    const petit = petitRef.current?.getBoundingClientRect();
    if (!grand || !petit) return;
    const cadre = (r: DOMRect) => ({ x: r.left, y: r.top, w: r.width, h: r.height });
    /*
      Une image que recouvre un portrait se range.

      Le portrait est peint par la page et suit le doigt dans l'instant ;
      l'image native arrive un aller-retour plus tard. Laissee sous le
      portrait, elle en depassait pendant un glissement : un rectangle noir
      courait derriere la vignette. Un cadre vide la cache, et la coquille
      peint la fenetre dessous du gris des portraits.
    */
    const vide = { x: 0, y: 0, w: 0, h: 0 };
    /*
      Zoomee, la grande image deborde de l'ecran : un cadre plus grand,
      centre sur ce qu'on regarde. L'image native remplit son cadre, et la
      fenetre coupe ce qui depasse — c'est tout le zoom, sans une ligne de
      Swift, donc sur toutes les coquilles.
    */
    const grandCadre = cadreZoome(cadre(grand), zoomRef.current);
    void placerLesVues(
      grandSansImage ? vide : grandCadre,
      petitSansImage ? vide : cadre(petit),
      grandEstMoi,
    ).then(() => {
      /*
        Le trou du grand portrait et l'ombre de la vignette suivent l'image
        native, et non le doigt.

        La page se deplace a l'instant ou le doigt bouge ; l'image native,
        un aller-retour de pont plus tard. Quand le trou suivait le doigt,
        il la devancait de plusieurs images pendant un glissement et
        laissait voir ce qu'il y avait dessous. Releve image par image sur
        une video de l'appareil : la camera decalee d'un tiers de vignette
        dans un rectangle noir, et l'ombre partie devant.

        Poses ici, une fois le natif servi, ils ne le devancent plus. Sur
        l'element directement et non par un etat : le placement est lui-meme
        declenche par le rendu, et un etat de plus rebouclerait.
      */
      const ecran = petitRef.current?.closest<HTMLElement>('.call');
      ecran?.style.setProperty('--natif-x', `${petit.left}px`);
      ecran?.style.setProperty('--natif-y', `${petit.top}px`);
    });
  }, [grandEstMoi, grandSansImage, petitSansImage]);

  /*
    Ou dessiner, et la vue web effacee le temps de l'appel.

    L'image native est posee *sous* la vue web : pour la voir, il faut que
    tout ce qui est au-dessus soit transparent — la page, le cadre, et le
    fond de l'ecran d'appel. Les ecrans de l'application, eux, se cachent :
    transparents, on les verrait par-dessus l'image.

    Les emplacements se remesurent a chaque changement de taille — une
    rotation, le clavier — et a chaque echange.
  */
  useEffect(() => {
    if (!video || !actif?.natif) return;

    /*
      Reduit, l'appel s'efface entierement : les deux vues natives se
      rangent et la page redevient opaque. Sans cela l'application restait
      cachee — c'est la meme regle qui la masque pour laisser voir
      l'image — et l'on se serait promene dans un ecran vide.
    */
    if (reduit) {
      const vide = { x: 0, y: 0, w: 0, h: 0 };
      void placerLesVues(vide, vide, false);
      return;
    }

    const racine = document.documentElement;
    racine.dataset.videoNatif = 'oui';

    const placer = placerLesImages;

    placer();
    const observateur = new ResizeObserver(placer);
    if (grandRef.current) observateur.observe(grandRef.current);
    if (petitRef.current) observateur.observe(petitRef.current);
    window.addEventListener('resize', placer);
    /* Le rendu distant n'arrive pas toujours avec la mise en page : on
       repose la question quelques fois, le temps que la piste existe. */
    const relances = [400, 1200, 2500].map((delai) => window.setTimeout(placer, delai));

    return () => {
      observateur.disconnect();
      window.removeEventListener('resize', placer);
      for (const t of relances) window.clearTimeout(t);
      delete racine.dataset.videoNatif;
    };
    /* `maCamera` en dependance : l'image de retour n'existe qu'une fois la
       camera ouverte, et il faut alors la reclamer au natif. */
  }, [video, actif, reduit, maCamera, placerLesImages]);

  useEffect(() => {
    const nom = nomRef.current;
    const dock = dockRef.current;
    if (!video || !nom || !dock) {
      setBornes(null);
      return;
    }
    const mesurer = () =>
      setBornes({
        haut: Math.round(nom.getBoundingClientRect().bottom) + VIGNETTE.marge,
        bas: Math.round(dock.getBoundingClientRect().top) - VIGNETTE.marge - VIGNETTE.hauteur,
        large: window.innerWidth,
      });
    mesurer();
    // Un nom qui change de longueur change de hauteur : c'est la taille
    // qu'on observe, les deux blocs ne bougent pas d'eux-memes.
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(nom);
    observateur.observe(dock);
    window.addEventListener('resize', mesurer);
    return () => {
      observateur.disconnect();
      window.removeEventListener('resize', mesurer);
    };
    /* Le nom lui-meme ne compte pas dans les dependances : l'observateur
       est pose sur un bloc qui survit au changement de nom, et c'est sa
       hauteur qui deplace la vignette, pas son texte. */
  }, [video, reduit]);

  /*
    La vignette a bouge : l'image native la suit.

    Un observateur de taille ne voit pas un rectangle qui se deplace sans
    changer de taille, et le doigt ne fait que le deplacer. On repose donc
    les deux images a chaque position — pendant le glissement, cela revient
    a une pose par frame, ce que le natif fait sans broncher : il ne fait
    qu'affecter un rectangle a une vue, sans animation.
  */
  useEffect(() => {
    if (!video || !actif?.natif || reduit) return;
    placerLesImages();
  }, [video, actif, reduit, vignette?.x, vignette?.y, placerLesImages]);

  /* La duree ne commence qu'une fois la voix passee, pas des la sonnerie. */
  useEffect(() => {
    if (!connecte) return;
    const timer = setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [connecte]);

  /*
    La trace de l'appel, deposee par celui qui a appele.

    Lui seul sait comment ca s'est termine : le telephone de l'autre n'a
    peut-etre jamais sonne, application fermee. C'est aussi pour cela que
    la trace vaut la peine — c'est le seul endroit ou l'appel manque
    laissera quelque chose a voir.

    On ne note rien quand c'est nous qu'on appelle, ni sur un appel qui a
    abouti sans qu'on ait rien a dire de plus qu'une duree.
  */
  const noterAppel = useCallback(
    (correspondantId: string, manque: boolean, video: boolean, secondesTenues: number) => {
      if (noteFaiteRef.current) return;
      noteFaiteRef.current = true;
      void messagesApi
        .noteCall(correspondantId, {
          missed: manque,
          video,
          durationMs: manque ? 0 : secondesTenues * 1000,
        })
        .catch(() => undefined);
    },
    [],
  );

  const terminer = useCallback(() => {
    // L'appel systeme ne se ferme pas tout seul : il tient tant qu'on ne
    // lui a pas dit que c'etait fini.
    prisRef.current = null;
    void fermerLAppelSysteme();
    setActif(null);
    setStatut(null);
    setConnecte(false);
    setMuet(false);
    setSansCamera(false);
    setCameraEnFace(false);
    /* La mise en page des images ne se transmet pas d'un appel au suivant :
       la vignette revient en haut a droite, et l'echange se defait. */
    setMoiEnGrand(false);
    zoomRef.current = SANS_ZOOM;
    setCameraAvant(true);
    dernierAppuiRef.current = null;
    setCoin('hd');
    setGlisse(null);
    setVideo(false);
    setMaCamera(false);
    setReduit(false);
    // La session revient a WebKit : sans cela le natif continuerait de
    // reposer sa route a chaque branchement d'ecouteur, apres l'appel.
    void relacherHautParleur().catch(() => undefined);
    setHautParleur(false);
    setSecondes(0);
    setQui(null);
  }, []);

  /*
    La tonalite d'attente.

    Cote appelant seulement, et seulement tant que ca sonne : l'ecran
    disait « Appel… » dans le silence complet, sans rien pour distinguer
    un appel qui part d'un appel qui ne part pas. Le bip s'arrete des que
    l'autre decroche — c'est le statut qui bascule, pas la connexion, pour
    ne pas laisser la tonalite courir par-dessus les premiers mots pendant
    que le lien s'etablit.
  */
  useEffect(() => {
    if (!actif || connecte || statut !== 'ringing' || actif.role !== 'caller') return;
    void demarrerLaSonnerie();
    return () => void arreterLaSonnerie();
  }, [actif, connecte, statut]);

  /*
    Personne ne decroche.

    L'autre bout peut avoir ferme l'application — on ne sonne que chez
    quelqu'un qui l'a ouverte, faute de notification VoIP — et rien alors
    ne serait jamais revenu : l'ecran restait sur « Appel… » sans fin. Au
    bout du meme delai que celui qui fait expirer une sonnerie chez le
    destinataire, on raccroche et on le dit.
  */
  useEffect(() => {
    if (!actif || connecte || statut !== 'ringing' || actif.role !== 'caller') return;
    const minuteur = window.setTimeout(() => {
      setErreur(t.appels.ecran.personneNaRepondu);
      void actif.hangUp().catch(() => undefined);
      terminer();
    }, SONNERIE_MAX_MS);
    return () => window.clearTimeout(minuteur);
  }, [actif, connecte, statut, terminer]);

  const handlers = useCallback(
    () => ({
      onStatus: (s: CallStatus) => {
        setStatut(s);
        /*
          L'appel se termine, quel qu'en soit le bout.

          C'est toujours l'appelant qui note — lui seul sait comment ca
          s'est passe — mais il doit le faire aussi quand c'est l'autre
          qui raccroche : sans cela, un appel tenu vingt minutes puis
          termine par celui qui avait decroche ne laissait rien.
        */
        const appel = actifRef.current;
        if ((s === 'declined' || s === 'ended') && appel?.role === 'caller') {
          noterAppel(appel.otherId, !connecteRef.current, videoRef.current, secondesRef.current);
        }
        if (s === 'declined' || s === 'ended') terminer();
      },
      onConnected: () => {
        setConnecte(true);
        // Le systeme affiche « appel en cours » tant qu'on ne lui dit pas
        // que la voix passe ; ensuite il compte, comme pour ses appels.
        void connecterLAppelSysteme();
      },
      onVideo: () => setVideo(true),
      onFlux: poserFlux,
      onCameraEnFace: setCameraEnFace,
      onEchec: (message: string) => {
        setErreur(message);
        terminer();
      },
    }),
    [terminer, noterAppel, poserFlux],
  );

  const appeler = useCallback(
    async (correspondant: Correspondant, avecVideo = false) => {
      if (actif) return;
      /*
        Les deux se sont appeles en meme temps.

        Deux appels partent alors, chacun sonnant chez l'autre, et
        decrocher l'un laisse l'autre sonner dans le vide. Appuyer sur
        « appeler » quelqu'un qui nous appelle deja veut dire la meme
        chose que decrocher : on decroche.
      */
      if (entrant && entrant.id === correspondant.id) return decrocher();

      setErreur(null);
      setQui(correspondant);
      setStatut('ringing');
      setVideo(avecVideo);
      setMaCamera(avecVideo);
      noteFaiteRef.current = false;
      try {
        const appel = await callsApi.place(correspondant.id, handlers(), avecVideo);
        setActif(appel);
        /*
          Le systeme apprend l'appel une fois qu'il est parti, pas avant.

          Il y a moins d'une seconde entre les deux, et cela evite
          d'afficher un appel systeme pour une tentative qui echoue —
          quelqu'un qu'on n'a pas le droit d'appeler, un micro refuse.
        */
        void ouvrirLAppelSysteme(correspondant.username, appel.callId, avecVideo);
      } catch (cause) {
        setStatut(null);
        setQui(null);
        setErreur(cause instanceof Error ? cause.message : t.appels.ecran.pasParti);
      }
    },
    [actif, handlers],
  );

  /**
   * Prendre l'appel.
   *
   * `depuisSysteme` dit d'ou vient le geste, et ce n'est pas un detail :
   * repondu par CallKit, l'appel systeme **devient** l'appel en cours et
   * doit rester ouvert — c'est lui qui tient la session audio. Repondu
   * dans l'application, il n'a plus lieu d'etre et doit se fermer, sans
   * quoi l'ecran du systeme continue de sonner par-dessus.
   */
  async function decrocher(depuisSysteme = false, callIdSysteme?: string) {
    if (!entrant) return;
    const { callId, id, username, avatarUrl } = entrant;
    /*
      Une reponse du systeme vise un appel precis.

      Elle peut arriver en retard — le systeme garde celle qu'on n'a pas
      encore reclamee, pour l'application qui se lance en decrochant — et
      elle ne doit alors surtout pas decrocher l'appel d'apres. Un appel
      qui se prend tout seul, c'est l'autre bout qui voit une conversation
      s'ouvrir sans que personne n'ait rien touche.
    */
    if (callIdSysteme && callIdSysteme !== callId) return;
    /*
      Pose avant tout le reste, et sans attendre : l'effacement de
      l'annonce arrive pendant la reponse, donc avant qu'elle aboutisse.
    */
    prisRef.current = callId;
    setEntrant(null);
    /*
      Accepter ici accepte aussi la-bas.

      On lui demande de repondre, pas de raccrocher : l'appel systeme
      devient la conversation et garde la session audio. La raccrocher la
      rendrait, et le son tomberait.
    */
    if (!depuisSysteme) void accepterLAppelSysteme(callId);
    setErreur(null);
    setQui({ id, username, avatarUrl });
    setStatut('accepted');
    setVideo(Boolean(entrant.video));
    setMaCamera(Boolean(entrant.video));
    // Celui qui decroche ne note pas : c'est l'appelant qui tient la trace.
    noteFaiteRef.current = true;

    /*
      Le visage de l'appelant, si la premiere lecture n'a pas abouti.

      La banniere pose « Quelqu'un » en attendant le profil ; repondre
      depuis le bouton vert du systeme devance souvent cette lecture, et
      un echec de reseau la laissait inachevee — on parlait alors une
      demi-heure a un inconnu sans visage.
    */
    if (username === t.appels.ecran.quelquun) {
      void usersApi
        .publicProfile(id)
        .then((profil) =>
          setQui((q) =>
            q && q.id === id
              ? { ...q, username: profil.user.username, avatarUrl: profil.user.avatarUrl }
              : q,
          ),
        )
        .catch(() => undefined);
    }
    try {
      setActif(await callsApi.answer(callId, handlers()));
    } catch (cause) {
      prisRef.current = null;
      /*
        L'appel systeme n'a plus rien a tenir.

        Decroche depuis son ecran, il devient la conversation — mais si la
        reponse echoue il n'y a pas de conversation, et il resterait
        ouvert : ecran du systeme affiche, et sa session audio en travers
        de l'appel suivant.
      */
      void fermerLAppelSysteme();
      setStatut(null);
      setQui(null);
      setErreur(cause instanceof Error ? cause.message : t.appels.ecran.pasAbouti);
    }
  }

  /*
    Ce que le systeme decide, quand il a sonne a notre place.

    CallKit affiche son propre ecran d'appel, avec ses deux boutons. Le
    vert et le rouge arrivent ici, et font exactement ce que feraient les
    notres — l'appel est le meme, seule l'interface a change de main.
  */
  useEffect(() => {
    return suivreLAppelSysteme({
      onRepondre: (callId) => void decrocher(true, callId),
      /* Le systeme sonne a son tour : les deux ecrans apparaissent
         ensemble, ce qui est tout l'objet de l'attente. */
      onSonne: (callId) => {
        const attente = attenteRef.current;
        if (attente?.appel.callId === callId) montrerLAppel(attente.appel);
      },
      /*
        Le bouton rouge du systeme, pour les deux sortes d'appel.

        Il ne servait qu'a refuser ce qui sonnait. Depuis qu'un appel
        sortant est annonce au systeme, le meme bouton peut raccrocher une
        conversation en cours — et il faut alors faire ce que ferait le
        notre. Tout se lit dans les references : cette fonction est posee
        une fois et survit aux rendus suivants.
      */
      onRaccrocher: () => {
        const appel = actifRef.current;
        if (!appel) return void refuser();
        if (appel.role === 'caller') {
          noterAppel(appel.otherId, !connecteRef.current, videoRef.current, secondesRef.current);
        }
        void appel.hangUp();
        terminer();
      },
    });
    /*
      Rebranche quand l'appel entrant change, et seulement alors : les
      deux fonctions se refont a chaque rendu, mais elles lisent `entrant`
      par fermeture — celle posee au montage raterait l'appel arrive
      ensuite.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrant?.callId]);

  async function refuser() {
    /* Le bouton rouge du systeme peut tomber pendant que notre banniere
       attend encore la sienne : l'appel a refuser est alors celui-la. */
    const callId = entrant?.callId ?? attenteRef.current?.appel.callId;
    if (!callId) return;
    prisRef.current = null;
    oublierLAttente();
    setEntrant(null);
    // Refuser ici refuse aussi la-bas.
    void refuserLAppelSysteme(callId);
    await callsApi.decline(callId).catch(() => undefined);
  }

  function raccrocher() {
    if (actif?.role === 'caller') {
      noterAppel(actif.otherId, !connecte, video, secondes);
    }
    void actif?.hangUp();
    terminer();
  }

  /* --- L'ecran ------------------------------------------------------- */

  const sonne = entrant !== null && actif === null;
  const enCours = statut !== null && entrant === null;
  const affiche = sonne ? entrant : qui;

  /*
    Un visage a la place du noir.

    Une camera coupee n'envoie plus rien, et la place qu'elle occupait
    devient un rectangle noir que rien n'explique — ni panne, ni choix. On
    y pose le portrait de celui qui manque : c'est ce que font les autres
    applications d'appel, et c'est la seule chose qui dise que la personne
    est toujours la.

    Les deux places ne bougent pas, ce sont leurs occupants qui
    s'echangent : la grande porte ma propre image tant que l'autre n'a pas
    decroche, et apres un echange.
  */
  const monPortrait = user ? { username: user.username, avatarUrl: user.avatarUrl } : null;
  const grandQui = grandEstMoi ? monPortrait : affiche;
  const petitQui = grandEstMoi ? affiche : monPortrait;

  /*
    Le doigt sur la vignette : un appui echange les deux images, un
    glissement la transporte.

    Les deux gestes partent du meme endroit, et rien ne les distingue avant
    que le doigt bouge : on attend donc huit points pour trancher. En
    dessous c'est un appui — un doigt ne se pose jamais parfaitement
    immobile, et sans ce seuil l'echange serait devenu impossible.

    Au relachement, la vignette rejoint le coin le plus proche de son
    centre. Elle ne glisse pas jusqu'a lui : l'image est dessinee par
    l'application, sous la page, et une transition CSS la laisserait
    derriere pendant que sa doublure avance sans elle.
  */
  const gesteVignette = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!vignette) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      departRef.current = { x: e.clientX, y: e.clientY, bouge: false };
    },
    onPointerMove: (e: React.PointerEvent) => {
      const depart = departRef.current;
      if (!depart) return;
      const dx = e.clientX - depart.x;
      const dy = e.clientY - depart.y;
      if (!depart.bouge && Math.hypot(dx, dy) < GLISSEMENT_MIN) return;
      depart.bouge = true;
      setGlisse({ dx, dy });
    },
    onPointerUp: (e: React.PointerEvent) => {
      const depart = departRef.current;
      departRef.current = null;
      setGlisse(null);
      if (!depart) return;
      if (!depart.bouge) return setMoiEnGrand((avant) => !avant);
      const arrivee = placeDeLaVignette(coin, {
        dx: e.clientX - depart.x,
        dy: e.clientY - depart.y,
      });
      if (!arrivee) return;
      // Le coin le plus proche de son centre, une fois le voyage borne.
      const bas = arrivee.y + VIGNETTE.hauteur / 2 > window.innerHeight / 2;
      const droite = arrivee.x + VIGNETTE.largeur / 2 > window.innerWidth / 2;
      setCoin(`${bas ? 'b' : 'h'}${droite ? 'd' : 'g'}` as Coin);
    },
    onPointerCancel: () => {
      departRef.current = null;
      setGlisse(null);
    },
  };

  /*
    Deux appuis sur ma propre image en grand retournent la camera.

    Seulement quand c'est elle qui occupe l'ecran : c'est la qu'on regarde
    ce qu'on filme, et un appui sur l'image de l'autre n'a pas a changer
    ce que je montre. Rien ne s'affiche pour un appui seul — l'ecran reste
    celui qu'on connait.
  */
  /** Pose le zoom : le cadre natif, ou la balise sur le chemin de la vue web. */
  const appliquerLeZoom = () => {
    if (actif?.natif) {
      if (imageZoomRef.current) return;
      imageZoomRef.current = requestAnimationFrame(() => {
        imageZoomRef.current = 0;
        placerLesImages();
      });
      return;
    }
    const balise = grandEstMoi ? mienneRef.current : distanteRef.current;
    if (!balise) return;
    const { s, tx, ty } = zoomRef.current;
    // Le miroir de sa propre camera de face, que la feuille de style porte.
    const miroir = grandEstMoi && cameraAvant ? ' scaleX(-1)' : '';
    balise.style.transform = s === 1 ? '' : `translate(${tx}px, ${ty}px) scale(${s})${miroir}`;
  };

  /** Le point de depart du geste, repris a chaque doigt qui arrive ou part. */
  const repartir = () => {
    const doigts = [...doigtsRef.current.values()];
    if (doigts.length >= 2) {
      const [a, b] = doigts;
      departZoomRef.current = {
        z: zoomRef.current,
        ecart: Math.hypot(a.x - b.x, a.y - b.y),
        point: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
    } else if (doigts.length === 1) {
      departZoomRef.current = { z: zoomRef.current, ecart: 0, point: doigts[0] };
    } else {
      departZoomRef.current = null;
    }
  };

  /*
    La grande image se zoome a deux doigts, et se promene a un seul une
    fois zoomee — le calcul est dans `utils/zoomPince.ts`, eprouve par ses
    tests. Revenu presque a un, le zoom se remet exactement a un.

    Rien a zoomer quand un portrait remplace l'image.
  */
  const zoomPossible = video && !grandSansImage;
  const gesteZoom = {
    onPointerDown: (e: React.PointerEvent) => {
      if (doigtsRef.current.size === 0) bougeRef.current = false;
      if (!zoomPossible) return;
      /* Chaque doigt reste attache a l'image, meme s'il passe sur le nom
         ou les boutons : sans cela il s'y perdrait, et sa levee avec lui. */
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Un pointeur deja parti : rien a retenir.
      }
      doigtsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (doigtsRef.current.size >= 2) bougeRef.current = true;
      repartir();
    },
    onPointerMove: (e: React.PointerEvent) => {
      const doigts = doigtsRef.current;
      if (!doigts.has(e.pointerId)) return;
      doigts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const depart = departZoomRef.current;
      const zone = e.currentTarget.closest('.call')?.getBoundingClientRect();
      if (!depart || !zone) return;
      const [a, b] = [...doigts.values()];

      if (b && depart.ecart > 0) {
        zoomRef.current = pincer(depart.z, depart.point, depart.ecart, a, b, zone);
      } else {
        if (Math.hypot(a.x - depart.point.x, a.y - depart.point.y) > GLISSEMENT_MIN) {
          bougeRef.current = true;
        }
        if (depart.z.s === 1) return;
        zoomRef.current = promener(depart.z, depart.point, a, zone);
      }
      appliquerLeZoom();
    },
    onPointerCancel: (e: React.PointerEvent) => {
      doigtsRef.current.delete(e.pointerId);
      repartir();
    },
  };

  const gesteGrand = {
    ...gesteZoom,
    onPointerUp: (e: React.PointerEvent) => {
      const seul = doigtsRef.current.size <= 1;
      doigtsRef.current.delete(e.pointerId);
      if (doigtsRef.current.size === 0 && zoomRef.current.s < ZOOM_RETOUR) {
        zoomRef.current = SANS_ZOOM;
        appliquerLeZoom();
      }
      repartir();
      // Un geste de zoom n'est pas un appui : il ne compte pas pour le double.
      if (!seul || bougeRef.current) return;
      if (!grandEstMoi || !maCamera || !actif) return;
      const precedent = dernierAppuiRef.current;
      const double =
        precedent !== null &&
        e.timeStamp - precedent.t < DOUBLE_APPUI.ms &&
        Math.hypot(e.clientX - precedent.x, e.clientY - precedent.y) < DOUBLE_APPUI.px;
      if (!double) {
        dernierAppuiRef.current = { t: e.timeStamp, x: e.clientX, y: e.clientY };
        return;
      }
      dernierAppuiRef.current = null;
      // Une coquille qui ne sait pas le faire rejette : le geste ne fait rien.
      void actif
        .retournerCamera()
        .then((ecran) => {
          setCameraAvant(ecran);
          /* Sur le chemin de la vue web, la piste a change sous la balise :
             on lui repose sa source pour qu'elle suive, comme a l'arrivee
             d'une piste. En natif, l'image est dessinee de l'autre cote. */
          const balise = mienneRef.current;
          if (actif.natif || !balise) return;
          balise.srcObject = null;
          balise.srcObject = actif.local;
          void balise.play().catch(() => undefined);
        })
        .catch(() => undefined);
    },
  };

  /*
    L'ecran d'appel est sombre quelle que soit l'heure du spot.

    C'est une exception assumee, comme les metaux du podium : un appel
    n'est pas une page de l'application, c'est l'ecran qu'on regarde quand
    le telephone est contre l'oreille. Tous les telephones le font sombre,
    et un fond sable en plein jour aurait l'air d'une erreur.
  */
  return (
    <Contexte.Provider value={{ appeler, occupe: actif !== null || statut !== null }}>
      {children}

      {(sonne || enCours) && !reduit && (
        <div
          className={`call${video ? ' call--video' : ''}${
            sonne ? ' call--entrant' : ''
          }`}
          /* Tant que rien n'est mesure, la feuille de style garde sa
             position de repli : la vignette en haut a droite, comme avant. */
          style={
            vignette
              ? ({ '--vignette-x': `${vignette.x}px`, '--vignette-y': `${vignette.y}px` } as CSSProperties)
              : undefined
          }
          role="dialog"
          aria-modal="true"
        >
          {/*
            En video, l'image de l'autre prend tout l'ecran et la mienne se
            pose en vignette. Les deux balises restent montees meme avant la
            connexion : le flux leur est confie des que l'appel existe, et
            une balise creee apres coup ne se remet pas toujours a jouer.
          */}
          {video && actif?.natif ? (
            <>
              {/*
                Deux emplacements vides : l'image passe derriere, dessinee
                par l'application. Ce qui reste ici — le nom, le
                chronometre, les boutons — garde le theme du spot au lieu
                d'etre redessine en Swift.
              */}
              <div ref={grandRef} className="call__distante call__place" {...gesteGrand} />
              {/* L'ombre de la vignette, la ou le natif a pose l'image, et
                  seulement par-dessus une image : un portrait de vignette
                  porte la sienne, et le grand portrait n'en veut pas. */}
              {!petitSansImage && !grandSansImage && (
                <div className="call__ombre" aria-hidden="true" />
              )}
              <button
                ref={petitRef}
                type="button"
                className="call__mienne call__place"
                aria-label={t.appels.ecran.echanger}
                {...gesteVignette}
              />
            </>
          ) : video ? (
            <>
              {/*
                Le meme echange que sur iOS, sans le natif : les deux
                balises gardent leur flux, seules leurs places permutent.
                Le bouton invisible occupe la vignette, ou qu'elle soit —
                detacher le geste des balises evite de le perdre quand
                l'une devient l'autre.
              */}
              <video
                ref={distanteRef}
                className={`call__distante${grandEstMoi ? ' call__distante--vignette' : ''}`}
                autoPlay
                playsInline
                /* Grande, elle se zoome ; en vignette, le bouton d'echange
                   la recouvre et rien ne lui parvient. */
                {...gesteGrand}
              />
              <video
                ref={mienneRef}
                className={`call__mienne${grandEstMoi ? ' call__mienne--plein' : ''}${
                  cameraAvant ? '' : ' call__mienne--dos'
                }`}
                autoPlay
                playsInline
                muted
                {...gesteGrand}
              />
              <button
                type="button"
                className="call__echange"
                aria-label={t.appels.ecran.echanger}
                {...gesteVignette}
              />
            </>
          ) : null}

          {/*
            Le portrait de celui dont l'image manque, a la place qu'elle
            occupait. Les deux voiles empruntent la geometrie des images
            qu'ils recouvrent — une seule feuille de style decide donc ou
            se pose une vignette — et laissent passer le doigt : dessous,
            c'est le bouton qui echange les deux places.
          */}
          {video && grandSansImage && (
            <div
              /* Perce a l'endroit de la vignette quand elle montre une
                 image native : voir `.call__voile--perce`. */
              className={`call__distante call__voile${
                actif?.natif && !petitSansImage ? ' call__voile--perce' : ''
              }`}
            >
              <Avatar
                user={{
                  username: grandQui?.username ?? '',
                  avatarUrl: grandQui?.avatarUrl ?? null,
                  online: false,
                }}
                size={132}
              />
            </div>
          )}
          {video && petitSansImage && (
            <div className="call__mienne call__voile">
              <Avatar
                user={{
                  username: petitQui?.username ?? '',
                  avatarUrl: petitQui?.avatarUrl ?? null,
                  online: false,
                }}
                size={54}
              />
            </div>
          )}

          {/* Reduire garde l'appel et rend l'application. */}
          {enCours && (
            <button
              className="call__reduce"
              aria-label={t.appels.ecran.reduire}
              onClick={() => setReduit(true)}
            >
              <CaretDown size={26} weight="bold" />
            </button>
          )}

          {sonne ? (
            /*
              L'appel entrant reprend la mise en page qu'on connait de
              l'ecran systeme : la nature de l'appel en petit tout en
              haut, le nom en grand dessous, et rien d'autre. Pas de
              portrait — on decroche ou l'on refuse, on ne consulte pas
              une fiche.
            */
            <div className="call__entrant">
              <p className="call__source">
                {entrant?.video ? t.appels.ecran.entrant.video : t.appels.ecran.entrant.audio}
              </p>
              <p className="call__appelant">{entrant?.username ?? t.appels.ecran.quelquun}</p>
              {/* Le visage arrive apres le nom : le profil se charge en second,
                  et un portrait qui apparait sous le nom ne deplace rien. */}
              <div className="call__portrait">
                <Avatar
                  user={{
                    username: entrant?.username ?? '',
                    avatarUrl: entrant?.avatarUrl ?? null,
                    // Pas de pastille verte : on sait deja qu'il est la.
                    online: false,
                  }}
                  size={132}
                />
              </div>
            </div>
          ) : (
            <div ref={nomRef} className="call__who">
              <Avatar
                user={{
                  username: affiche?.username ?? '',
                  avatarUrl: affiche?.avatarUrl ?? null,
                  // La pastille verte ne s'affiche pas ici : on lui parle.
                  online: false,
                }}
                size={132}
              />
              <p className="call__name">{affiche?.username ?? t.appels.ecran.appel}</p>
              <p className="call__state">
                {connecte
                  ? duree(secondes)
                  : statut === 'accepted'
                    ? t.appels.ecran.connexion
                    : video
                      ? t.appels.ecran.appelVideo
                      : t.appels.ecran.appelEnCours}
              </p>
            </div>
          )}

          {/* Meme raison : pas de balise du tout quand le son est natif. */}
          {enCours && !actif?.natif && <audio ref={audioRef} autoPlay playsInline />}

          <div ref={dockRef} className="call__dock">
            {sonne ? (
              <>
                <span className="call__choix">
                  <button
                    className="call__round call__round--refuse"
                    aria-label={t.appels.ecran.refuser}
                    onClick={() => void refuser()}
                  >
                    <X size={30} weight="bold" />
                  </button>
                  <span className="call__choix__mot">{t.appels.ecran.refuser}</span>
                </span>
                {/*
                  Repondre a une camera n'est pas repondre a un combine :
                  on va montrer son visage. Le bouton le dit avant qu'on
                  appuie, pas apres.
                */}
                <span className="call__choix">
                  <button
                    className="call__round call__round--accept"
                    aria-label={entrant?.video ? t.appels.ecran.repondreVideo : t.appels.ecran.repondre}
                    onClick={() => void decrocher()}
                  >
                    <Check size={30} weight="bold" />
                  </button>
                  <span className="call__choix__mot">{t.appels.ecran.accepter}</span>
                </span>
              </>
            ) : (
              <>
                {/*
                  Les commandes des l'appui, pas une fois la voix passee.

                  Elles n'apparaissaient qu'a la connexion : celui qui
                  appelle n'avait que le bouton rouge sous les yeux, et
                  devait attendre qu'on lui reponde pour se mettre sur le
                  haut-parleur ou en silencieux. Or c'est justement
                  pendant que ca sonne qu'on prepare l'appel — on pose le
                  telephone sur la table, on coupe le micro le temps de
                  finir une phrase, on allume sa camera pour se montrer
                  des le premier bonjour.

                  Rien n'apparait donc plus en cours de route : la rangee
                  est la du debut a la fin, et c'est l'etat des boutons
                  qui change, pas leur presence.

                  Des l'appui, donc, et non des que l'appel est parti :
                  entre les deux il y a le micro a demander, l'offre a
                  fabriquer, la base a ecrire. Une seconde pendant laquelle
                  la rangee apparaitrait quand meme en cours de route. Elle
                  est donc la tout de suite, en attente, et s'anime quand
                  l'appel existe.
                */}
                {
                  <div className="call__pill">
                    <button
                      className={`call__tool${muet ? ' call__tool--on' : ''}`}
                      aria-label={muet ? t.appels.ecran.reactiverMicro : t.appels.ecran.couperMicro}
                      disabled={!actif}
                      onClick={() => actif && setMuet(actif.toggleMute())}
                    >
                      {muet ? <MicrophoneSlash size={22} /> : <Microphone size={22} />}
                    </button>

                    {/*
                      Passer en visio sans raccrocher. Le bouton disparait
                      des que la camera est de la partie — il laisse alors
                      la place a celui qui la coupe.
                    */}
                    {!maCamera && (
                      <button
                        className="call__tool"
                        aria-label={t.appels.ecran.montrerCamera}
                        disabled={!actif}
                        onClick={() =>
                          actif &&
                          void actif
                            .passerEnVideo()
                            .then(() => setMaCamera(true))
                            .catch(() => setErreur(t.appels.ecran.cameraPasOuverte))
                        }
                      >
                        <VideoCamera size={22} />
                      </button>
                    )}

                    {/* Sa propre camera, coupable sans quitter l'appel. */}
                    {maCamera && actif && (
                      <button
                        className={`call__tool${sansCamera ? ' call__tool--on' : ''}`}
                        aria-label={sansCamera ? t.appels.ecran.rallumerCamera : t.appels.ecran.couperCamera}
                        onClick={() => setSansCamera(actif.toggleCamera())}
                      >
                        {sansCamera ? <VideoCameraSlash size={22} /> : <VideoCamera size={22} />}
                      </button>
                    )}

                    {/*
                      Le haut-parleur n'existe que la ou le son se route,
                      c'est-a-dire dans l'application native : sur le web,
                      c'est le systeme qui decide et le bouton mentirait.
                    */}
                    {hautParleurDisponible() && (
                      <button
                        className={`call__tool${hautParleur ? ' call__tool--on' : ''}`}
                        aria-label={
                          hautParleur ? t.appels.ecran.ecouteur : t.appels.ecran.hautParleur
                        }
                        disabled={!actif}
                        onClick={() => poserLaRoute(!hautParleur)}
                      >
                        {/* Une seule icone : le fond blanc dit l'etat. Un
                            haut-parleur barre laissait croire a une panne
                            plutot qu'a un choix. */}
                        <SpeakerHigh size={22} />
                      </button>
                    )}
                  </div>
                }
                <button
                  className="call__round call__round--refuse"
                  aria-label={t.appels.ecran.raccrocher}
                  onClick={raccrocher}
                >
                  <Combine />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reduit : un bloc qu'on range ou l'on veut, et qui rouvre l'appel. */}
      {enCours && reduit && (
        <BulleAppel
          nom={affiche?.username ?? t.appels.ecran.appel}
          avatarUrl={affiche?.avatarUrl ?? null}
          etat={connecte ? duree(secondes) : t.appels.ecran.appelEnCours}
          onOuvrir={() => setReduit(false)}
        />
      )}

      {erreur && (
        <p className="call__erreur" onClick={() => setErreur(null)}>
          {erreur}
        </p>
      )}
    </Contexte.Provider>
  );
}
